import { Popover } from '@headlessui/react'
import { SearchIcon } from '@heroicons/react/solid'
import { Placement } from '@popperjs/core'
import React, { ChangeEvent, ReactElement, useEffect, useState } from 'react'
import { usePopper } from 'react-popper'
import { GInput } from '../g-input'
import { GSlideOver } from '../g-slide-over'
import { GTransition } from '../g-transition'

interface GSwitcherProps<T extends { name: string }> {
	items: any
	accounts?: T[]
	selectedItem: any
	footer: React.ReactNode
	header?: React.ReactNode
	filter?: (i: T, s: string) => boolean
	renderOption: (
		i: T,
		handleClose: () => void
	) => React.ReactNode
	placeholder: string
	responsive?: boolean
	disableSearch?: boolean
	width?: string
	position?: Placement
	offset?: [number, number]
	deepSearch?: boolean
}

export const GSwitcher = <T extends { name: string }>(
	props: GSwitcherProps<T>
) => {
	const {
		items,
		selectedItem,
		width,
		footer,
		renderOption,
		placeholder,
		filter,
		responsive = false,
		disableSearch = false,
		position,
		offset,
		header,
		deepSearch = false
	} = props

	const [open, setOpen] = useState(false)
	const [referenceElement, setReferenceElement] =
		useState<HTMLDivElement | null>(null)
	const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
		null
	)
	const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null)
	const [searchQuery, setSearchQuery] = useState('')
	const [filteredItems, setFilteredItems] = useState<T[]>(items)
	const { styles, attributes } = usePopper(referenceElement, popperElement, {
		modifiers: [
			{ name: 'arrow', options: { element: arrowElement } },
			{
				name: 'offset',
				options: {
					offset: offset || [0, 0]
				}
			}
		],
		placement: position || 'bottom-start'
	})

	useEffect(() => {
		setFilteredItems(items)
	}, [items])

	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(e.target.value)
		filterList(e.target.value)
	}

	const resetChanges = () => {
		setSearchQuery('')
		setFilteredItems(items)
	}

	const handleClose = (close: () => void) => {
		return () => {
			resetChanges()
			close()
		}
	}

	const filterList = (e: string) => {
		if (deepSearch) {
			// filter the items and their asset and return the parent item with only the assets that match the query
			const filteredAssets = items.filter(
				(item: { name: string; assets: any[] }) =>
					item.name.toLowerCase().includes(e.toLowerCase()) ||
					item.assets?.some((asset) =>
						asset.name.toLowerCase().includes(e.toLowerCase())
					) ||
					false
			)
			// if the items have assets, filter them too
			const filteredAssetsWithAssets = filteredAssets.map(
				(item: { assets: any[] }) => {
					if (item.assets) {
						const filteredAssets = item.assets.filter((asset) =>
							asset.name.toLowerCase().includes(e.toLowerCase())
						)
						return { ...item, assets: filteredAssets }
					}
					return item
				}
			)

			setFilteredItems(filteredAssetsWithAssets)
		} else {
			const filteredOptions =
				searchQuery === '' || !filter
					? items
					: items.filter((i: T) => filter(i, e))
			setFilteredItems(filteredOptions)
		}
	}

	if (responsive)
		return (
			<>
				<div
					onClick={() => {
						setOpen(true)
					}}
				>
					{selectedItem}
				</div>
				<GSlideOver
					position="right"
					open={open}
					setOpen={setOpen}
					className="flex md:hidden z-51 "
				>
					<div className="w-72 p-4 z-50 overflow-y-auto overflow-x-hidden">
						<div className="flex flex-col">
							<div className="flex-1 flex flex-col">
								{/* <Header /> */}
								<div className="flex-1 flex flex-col mb-2">
									{/* <Body /> */}
								</div>
								{/* {footer && <div className="flex border-t">
								{footer}
							</div>} */}
							</div>
						</div>
					</div>
				</GSlideOver>
			</>
		)

	return (
		<Popover className="w-full">
			{() => (
				<>
					<Popover.Button as="div" className={'w-full ml-0 top-6'}>
						<div ref={setReferenceElement} className="w-full">
							{selectedItem}
						</div>
					</Popover.Button>
					<Popover.Panel
						ref={setPopperElement}
						style={styles.popper}
						{...attributes.popper}
						className={`bg-popover ${width}  shadow-dropShadow rounded-3xl absolute z-20 ml-0 !left-10 mt-2`}
					>
						{({ close }) => (
							<>
								<GTransition show={true}>
									<div className="flex flex-col">
										<div className="flex-1 flex flex-col overflow-hidden rounded-3xl">
											{!disableSearch && (
												<div className="bg-white-100 p-2">
													<GInput
														value={searchQuery}
														icon={SearchIcon}
														placeholder={placeholder}
														onChange={(e) => handleChange(e)}
													/>
												</div>
											)}
											{header && <div>{header}</div>}
											<div className="flex-1 flex flex-col rounded-md mr-1 max-h-56 overflow-auto">
												{filteredItems.length > 0 ? (
													<div className="mx-2">
														{filteredItems?.map(
															(item) =>
																renderOption &&
																renderOption(item, handleClose(close))
														)}
													</div>
												) : (
													<div className="flex justify-center items-center text-t-secondary">
														No items
													</div>
												)}
											</div>
											{footer && <div className="flex">{footer}</div>}
										</div>
									</div>
								</GTransition>
								<div ref={setArrowElement} style={styles.arrow} />
							</>
						)}
					</Popover.Panel>
				</>
			)}
		</Popover>
	)
}
